import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAppSelector } from '@hooks/redux';
import { Box, CircularProgress } from '@mui/material';

interface ProtectedRouteProps {
  requireRole?: 'admin' | 'operator' | 'viewer';
  redirectTo?: string;
  children?: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  requireRole,
  redirectTo = '/login',
  children
}) => {
  const location = useLocation();
  const { isAuthenticated, user, isInitialized } = useAppSelector((state) => state.auth);

  // Wait for auth initialization
  if (!isInitialized) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh'
        }}>
        <CircularProgress />
      </Box>
    );
  }

  // Check if user is authenticated
  if (!isAuthenticated) {
    // Redirect to login page but save the attempted location
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check role-based access if required
  if (requireRole && user) {
    const roleHierarchy = {
      viewer: 1,
      operator: 2,
      admin: 3,
    };

    const userRoleLevel = roleHierarchy[user.role] || 0;
    const requiredRoleLevel = roleHierarchy[requireRole] || 0;

    if (userRoleLevel < requiredRoleLevel) {
      // User doesn't have sufficient permissions
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // User is authenticated and has required role
  return children ? <>{children}</> : <Outlet />;
};

export default ProtectedRoute;
